import { WHMessagingDB } from "./db";
import { BackgroundRequest, BackgroundQueueRecord, SendMultiRequest, SetConversationCategoryRequest, ToggleUserCategorySubscriptionRequest, QueuedMediaMessageItem } from "./background-queue.types";
import { libSdk, SendMultiMessageItem as SdkSendMultiMessageItem } from "./sdk";
import { libMisc } from "./misc";

const RETRY_DELAY_MS = 5000; // Delay before retrying the queue if an item fails

export class BackgroundQueueProcessor {
    private isProcessing = false;
    private db: WHMessagingDB;

    constructor(db: WHMessagingDB) {
        this.db = db;
    }

    public async queue(request: BackgroundRequest): Promise<void> {
        const requestJson = JSON.stringify(request);
        await this.db.backgroundQueue.add({ requestJson });
        this.process(); // Trigger processing if not already active
    }

    public async process(): Promise<void> {
        if (this.isProcessing) {
            return;
        }
        this.isProcessing = true;

        try {
            while (true) {
                const nextRequestRecord = await this.db.backgroundQueue.orderBy('id').first();
                if (!nextRequestRecord || typeof nextRequestRecord.id === 'undefined') {
                    break; // Queue is empty
                }

                let requestData: BackgroundRequest;
                try {
                    requestData = JSON.parse(nextRequestRecord.requestJson) as BackgroundRequest;
                } catch (parseError) {
                    console.error("Failed to parse background request JSON, removing from queue:", nextRequestRecord.requestJson, parseError);
                    await this.db.backgroundQueue.delete(nextRequestRecord.id);
                    continue; // Process next item
                }

                let success = false;
                const blobIdsToDelete: number[] = [];

                try {
                    if (requestData.type === "setConversationCategory") {
                        const req = requestData as SetConversationCategoryRequest;
                        success = await libSdk.setConversationCategory(req.convId, req.categoryId);
                    } else if (requestData.type === "toggleUserCategorySubscription") {
                        const req = requestData as ToggleUserCategorySubscriptionRequest;
                        success = await libSdk.toggleUserCategorySubscription(req.categoryId, req.state);
                    } else if (requestData.type === "sendMulti") {
                        const req = requestData as SendMultiRequest;
                        const messagesForSdk: SdkSendMultiMessageItem[] = [];
                        let allBlobsRetrieved = true;

                        for (const item of req.messages) {
                            if (item.type === "media") {
                                const mediaVal = item.value as { blobId: number; filename: string };
                                const queuedBlobRecord = await this.db.queuedMediaBlobs.get(mediaVal.blobId);
                                if (queuedBlobRecord) {
                                    messagesForSdk.push({
                                        type: "media",
                                        value: { blob: queuedBlobRecord.blob, filename: mediaVal.filename },
                                        tmp_id: item.tmp_id,
                                        direction: item.direction
                                    });
                                    blobIdsToDelete.push(mediaVal.blobId); // Mark for potential deletion
                                } else {
                                    console.error(`sendMulti background task: Blob with id ${mediaVal.blobId} not found.`);
                                    allBlobsRetrieved = false;
                                    break;
                                }
                            } else {
                                messagesForSdk.push({
                                    type: "text",
                                    value: item.value as string,
                                    tmp_id: item.tmp_id,
                                    direction: item.direction
                                });
                            }
                        }

                        if (allBlobsRetrieved) {
                            success = await libSdk.sendMulti(req.convId, messagesForSdk);
                        } else {
                            // If blobs couldn't be retrieved, this is a permanent failure for this item.
                            // It shouldn't block the queue indefinitely.
                            console.error(`sendMulti background task: Failed to retrieve all blobs for request id ${nextRequestRecord.id}. Removing from queue.`);
                            // Consider what to do with blobIdsToDelete if some were retrieved but not all.
                            // For now, we don't delete them if the overall operation fails here.
                            await this.db.backgroundQueue.delete(nextRequestRecord.id); // Remove the problematic item
                            continue; // Process next item
                        }
                    } else {
                        console.error("Unknown background request type, removing from queue:", requestData);
                        await this.db.backgroundQueue.delete(nextRequestRecord.id); // Remove unknown type
                        continue; // Process next item
                    }
                } catch (sdkError) { // This catch is for unexpected errors in libSdk calls, not for false returns
                    console.error("Unexpected error during SDK call in background processing:", sdkError, requestData);
                    success = false; // Ensure it's marked as failed for retry
                }

                if (success) {
                    await this.db.backgroundQueue.delete(nextRequestRecord.id);
                    if (requestData.type === "sendMulti" && blobIdsToDelete.length > 0) {
                        // Delete successfully processed blobs
                        try {
                            await this.db.queuedMediaBlobs.bulkDelete(blobIdsToDelete);
                        } catch (deleteError) {
                            console.error("Error deleting queuedMediaBlobs after successful sendMulti:", deleteError, blobIdsToDelete);
                        }
                    }
                } else {
                    // SDK call returned false (meaning its internal retries failed) or an unexpected error occurred
                    console.warn("Background request failed or SDK returned false, will retry processing queue later:", requestData);
                    // The item remains in the queue. We'll pause before trying to process the queue again.
                    await libMisc.wait(RETRY_DELAY_MS);
                    // After the delay, the loop will re-fetch the oldest item. If this one is still the oldest and failing,
                    // it will be retried. If new items were added, they'd be processed if older.
                }
            }
        } catch (error) {
            console.error("Error in BackgroundQueueProcessor outer process loop:", error);
        } finally {
            this.isProcessing = false;
        }
    }
}